import type { CSSProperties } from "react";
import type {
  AppointmentPayment,
  AppointmentSummary,
  PaymentCurrency,
} from "../../../../hooks/agenda";

export const PAYMENT_CURRENCIES: PaymentCurrency[] = ["EUR", "USD", "CHF"];

/** Cents; the payment module refuses more than 5,000.00. */
export const PAYMENT_LIMITS = { minAmount: 100, maxAmount: 500000 } as const;

export const formatPrice = (
  amount: number,
  currency: string,
  lang: string
): string =>
  new Intl.NumberFormat(lang, { style: "currency", currency }).format(
    amount / 100
  );

/** Null when only the status of the payment is left: `formatPrice` throws without a currency. */
export const paymentPrice = (
  payment: Pick<AppointmentPayment, "amount" | "currency"> | null | undefined,
  lang: string
): string | null =>
  typeof payment?.amount === "number" && payment.currency
    ? formatPrice(payment.amount, payment.currency, lang)
    : null;

export const centsToInput = (amount: number, lang: string): string =>
  new Intl.NumberFormat(lang, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(amount / 100);

/** "45", "45,5" and "45.50" all read 4550; anything else, more than two decimals included, reads null. */
// Thousands as written in French or Swiss: a space, a no-break or narrow no-break space, an apostrophe.
const THOUSANDS = /[\s\u00A0\u202F'\u2019]/g;

export const inputToCents = (value: string): number | null => {
  const normalized = value.replace(THOUSANDS, "").replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  return Math.round(parseFloat(normalized) * 100);
};

export type PaymentView = "pending" | "due" | "paid";

/** `pending`: held while the client pays, so it can only be deleted. */
export const paymentViewOf = (
  appointment: Pick<AppointmentSummary, "status" | "payment">
): PaymentView | null =>
  appointment.status === "pending"
    ? "pending"
    : appointment.payment?.status ?? null;

/** A list cannot tell it is settled: reading the appointment does, for a payment made since. */
export const awaitsPayment = (
  appointment: Pick<AppointmentSummary, "status" | "payment">
): boolean => {
  const view = paymentViewOf(appointment);
  return view === "pending" || view === "due";
};

/** Stripes in the current text colour, laid over a booking waiting for its payment. */
export const PENDING_HATCH: CSSProperties = {
  backgroundImage:
    "repeating-linear-gradient(135deg, transparent, transparent 5px, currentColor 5px, currentColor 6px)",
};
